import clsx from 'clsx';
import openpathConfig from 'openpathConfig';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { CobaltChatSvg } from 'components/svgs/CobaltChatSvg';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { useHasPackages } from 'utils/customHooks/useHasPackages';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getOpParams } from 'utils/getOpParams';
import { useAppContext } from 'new-components/OpAppScaffold/components/AppContext';

import './CobaltChatButton.scss';

interface CobaltChatButtonProps {
  onClick: () => void;
  isActive?: boolean;
}

export const generateDjb2Hash = (str: string) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0; // unsigned 32-bit
};

export const isOrgInBetaBucket = (
  orgId: number,
  rolloutPercentage: number,
  enabledOrgIds: number[],
) => {
  if (enabledOrgIds.includes(orgId)) {
    return true;
  }
  // Deliberately 'prod' and 'prodeu1' only -- Cobalt is not launched in the
  // other regions yet. Do NOT replace with isProdEnv(): that would silently
  // start bucketing prodcan1/prodaus1 orgs by rollout percentage.
  if (openpathConfig.ENV === 'prod' || openpathConfig.ENV === 'prodeu1') {
    const bucket = generateDjb2Hash(orgId.toString()) % 100;
    return bucket < rolloutPercentage;
  }
  return true;
};

export const CobaltChatButton = ({
  onClick,
  isActive,
}: CobaltChatButtonProps) => {
  const { orgId } = getOpParams();
  const { app } = useAppContext();

  const { value: isCobaltEnabled } = useFeatureFlag<boolean>(
    'SHOW_COBALT_CHAT',
    orgId,
  );

  const { isValid: hasCobaltReadOrWriteScopes } = useValidateScopes([
    'o{orgId}-cobalt:r',
    'o{orgId}-cobalt:w',
  ]);

  const isAccessApp = app === 'access';

  const alphaCheck =
    isCobaltEnabled && hasCobaltReadOrWriteScopes && isAccessApp;

  const { value: isBetaFlowEnabled } = useFeatureFlag<boolean>(
    'ENABLE_AVIGILON_ASSIST_BETA',
    orgId,
  );

  const { value: cobaltBetaList } = useFeatureFlag<{
    rolloutPercentage: number;
    orgIds: number[];
  }>('COBALT_BETA_ENABLED_LIST', orgId);

  const cobaltBetaRolloutPercentage = cobaltBetaList?.rolloutPercentage ?? 5;

  const isPremiumOrEnterprise = useHasPackages(orgId, [
    'Premium',
    'Enterprise',
  ]);

  const { userId } = useIdentityAndUser();

  const { data: userRoles } = useOpQuery({
    apiEndpointName: 'listUserRoles',
    parameters: [orgId, userId!],
    select: (data: { json: { data: { id: number }[] } }) => data.json.data,
    enabled: Boolean(orgId && userId),
  });

  // this id comes from the helium migrations
  const USER_SUPER_ADMIN_ROLE_IDS = [5, 17, 32, 33];

  const hasSuperAdminRole =
    userRoles?.some(({ id }) => USER_SUPER_ADMIN_ROLE_IDS.includes(id)) ??
    false;

  // Beta opt-in flow — display the Cobalt chat icon only when ALL of:
  // 1. The beta feature flag is enabled
  // 2. The user's org is on a Premium or Enterprise package
  // 3. The user has a Super Admin role (any of the 4 variants)
  const betaCheck =
    isOrgInBetaBucket(
      orgId,
      cobaltBetaRolloutPercentage,
      cobaltBetaList?.orgIds ?? [],
    ) &&
    isPremiumOrEnterprise &&
    hasSuperAdminRole &&
    isAccessApp;

  const shouldShow = alphaCheck || (isBetaFlowEnabled && betaCheck);

  if (!shouldShow) return null;

  return (
    <OpButton
      className={clsx('cobalt-chat-button', {
        'cobalt-chat-button--active': isActive,
      })}
      icon={<CobaltChatSvg />}
      onClick={onClick}
      type="text"
      size="middle"
      data-testid="cobalt-chat-button"
    />
  );
};
